<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Descuentos y adicionales</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
          <div class="flex items-center gap-2 ml-2">
            <wz-checkbox switch v-model="showGroups" @change="onToggleGroups" />
            <span class="text-sm">Ver grupos de cuotas</span>
          </div>
        </template>

        <template #right>
          <wz-tooltip :text="showGroups ? 'Recargar grupos' : 'Recargar'">
            <wz-button variant="outline" @click="loadData">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-button variant="primary" @click="$router.push({ name: 'payroll-movement-create' })">
            <i class="ri-add-line mr-1"></i>Agregar
          </wz-button>
          <wz-button variant="secondary" @click="$router.push({ name: 'payroll-movement-bulk' })">
            <i class="ri-group-line mr-1"></i>Masivo
          </wz-button>

          <!-- Grupo: carga masiva por Excel -->
          <div class="flex gap-2">
            <wz-button variant="outline" :disabled="downloadingTemplate" @click="downloadTemplate">
              <i class="ri-download-line mr-1"></i>{{ downloadingTemplate ? 'Descargando...' : 'Descargar plantilla' }}
            </wz-button>
            <wz-button variant="outline" @click="openUploadModal">
              <i class="ri-upload-2-line mr-1"></i>Cargar Excel
            </wz-button>
          </div>

          <span class="toolbar-divider"></span>

          <!-- Grupo: exportar -->
          <div class="flex gap-2">
            <wz-button variant="outline" @click="$router.push({ name: 'payroll-movement-summary' })">
              <i class="ri-file-list-3-line mr-1"></i>Resumen
            </wz-button>
            <wz-button v-if="!showGroups" variant="outline" :disabled="downloading" @click="downloadExcel">
              <i class="ri-file-excel-line mr-1"></i>{{ downloading ? 'Descargando...' : 'Excel' }}
            </wz-button>
          </div>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <!-- ============================================================= -->
      <!-- VISTA NORMAL                                                    -->
      <!-- ============================================================= -->
      <template v-if="!showGroups">
        <wz-table>
          <thead>
            <tr>
              <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
                Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
              </th>
              <th>DNI</th>
              <th>Empresa</th>
              <th>Concepto</th>
              <th>Tipo</th>
              <th>Naturaleza</th>
              <th style="text-align: right">Monto</th>
              <th>Cuota</th>
              <th>Origen</th>
              <th>Estado</th>
              <th>Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="list.length === 0">
              <td colspan="11" class="text-center text-muted py-4">No se encontraron movimientos para el período seleccionado.</td>
            </tr>
            <tr v-for="item in sortedList(list)" :key="item.id">
              <td>{{ item.employeeName }}</td>
              <td>{{ item.employeeIdentifier }}</td>
              <td>{{ item.companyName }}</td>
              <td>{{ item.conceptName }}</td>
              <td>
                <wz-badge :variant="item.conceptType === 'DESCUENTO' ? 'danger-solid' : 'success-solid'" pill>
                  {{ getTypeLabel(item.conceptType) }}
                </wz-badge>
              </td>
              <td>{{ getNatureLabel(item.conceptNature) }}</td>
              <td style="text-align: right">{{ formatAmount(item.amount, item.currency) }}</td>
              <td>{{ item.installmentLabel || '-' }}</td>
              <td>{{ getOriginLabel(item.origin) }}</td>
              <td>
                <wz-badge :variant="statusVariant(item.status)" pill>{{ item.status }}</wz-badge>
              </td>
              <td style="text-align: center">
                <wz-dropdown
                  :items="movementActions(item)"
                  placement="left"
                  @click="handleMovementAction(item, $event)"
                />
              </td>
            </tr>
          </tbody>
        </wz-table>

        <wz-table-footer>
          <span>{{ list.length }} registro(s)</span>
        </wz-table-footer>
      </template>

      <!-- ============================================================= -->
      <!-- VISTA GRUPOS                                                    -->
      <!-- ============================================================= -->
      <template v-else>
        <wz-table>
          <thead>
            <tr>
              <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
                Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
              </th>
              <th>DNI</th>
              <th>Empresa</th>
              <th>Concepto</th>
              <th style="text-align: right">Monto total</th>
              <th>Cuotas</th>
              <th style="text-align: right">Saldo</th>
              <th>Estado</th>
              <th>Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="groups.length === 0">
              <td colspan="9" class="text-center text-muted py-4">No se encontraron grupos de cuotas.</td>
            </tr>
            <tr v-for="g in sortedList(groups, 'groupId')" :key="g.groupId">
              <td>{{ g.employeeName }}</td>
              <td>{{ g.employeeIdentifier }}</td>
              <td>{{ g.companyName }}</td>
              <td>{{ g.conceptName }}</td>
              <td style="text-align: right">{{ formatAmount(g.totalAmount, g.currency) }}</td>
              <td>{{ g.paidCount }}/{{ g.totalInstallments }}</td>
              <td style="text-align: right">{{ formatAmount(g.pendingBalance, g.currency) }}</td>
              <td>
                <wz-badge :variant="groupStatusVariant(g.status)" pill>{{ getGroupStatusLabel(g.status) }}</wz-badge>
              </td>
              <td style="text-align: center">
                <wz-dropdown
                  :items="[{ key: 'detail', label: 'Ver detalle' }]"
                  placement="left"
                  @click="$router.push({ name: 'movement-group-detail', params: { groupId: g.groupId } })"
                />
              </td>
            </tr>
          </tbody>
        </wz-table>

        <wz-table-footer>
          <span>{{ groups.length }} grupo(s)</span>
        </wz-table-footer>
      </template>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />

    <!-- Modal: Cargar Excel de movimientos (componente compartido con el Motor) -->
    <movement-bulk-upload-modal
      v-model="showUploadModal"
      :show-template-button="false"
      @uploaded="loadData"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin, FilterMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { PayrollMovementService, PayrollConceptService, CompanyService } from '@/core/services'
import MovementBulkUploadModal from '../Components/MovementBulkUploadModal.vue'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const MOVEMENT_STATUS_OPTIONS = [
  { key: 'PENDIENTE', label: 'Pendiente' },
  { key: 'PROCESADO', label: 'Procesado' },
  { key: 'CANCELADO', label: 'Cancelado' },
]

export default {
  name: 'PayrollMovementList',
  components: { MovementBulkUploadModal },
  mixins: [SortMixin, ConfirmModalMixin, FilterMixin],

  data() {
    const now = new Date()
    const currentYear = now.getFullYear()
    return {
      list:           [],
      groups:         [],
      showGroups:     false,
      downloading:    false,
      downloadingTemplate: false,
      showUploadModal: false,
      // Filtros manejados por el componente Filtrar (wz-filter-menu + FilterMixin).
      // Empresa y Concepto se rellenan en created(). Mes/Año arrancan en el período actual.
      filterOptions: [
        { label: 'Empresa',  key: 'companyId', type: LocalConstants.TypeFilters.SINGLE, options: [] },
        { label: 'Tipo',     key: 'type',      type: LocalConstants.TypeFilters.SINGLE, options: Object.values(LocalConstants.PayrollConceptType).map(t => ({ key: t.key, label: t.label })) },
        { label: 'Concepto', key: 'conceptId', type: LocalConstants.TypeFilters.SINGLE, options: [] },
        { label: 'Estado',   key: 'status',    type: LocalConstants.TypeFilters.SINGLE, options: MOVEMENT_STATUS_OPTIONS },
        { label: 'Mes',      key: 'month',     type: LocalConstants.TypeFilters.SINGLE, options: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })) },
        { label: 'Año',      key: 'year',      type: LocalConstants.TypeFilters.SINGLE, options: Array.from({ length: 5 }, (_, i) => ({ key: currentYear - 2 + i, label: String(currentYear - 2 + i) })) },
      ],
      filters: {
        month: now.getMonth() + 1,
        year:  currentYear,
      },
    }
  },

  methods: {
    statusVariant(status) {
      const map = { PENDIENTE: 'warning-solid', PROCESADO: 'success-solid', CANCELADO: 'dark' }
      return map[status] || 'dark'
    },
    groupStatusVariant(status) {
      return LocalConstants.InstallmentGroupStatus[status]?.color || 'dark'
    },
    // Período opcional: si el filtro no trae mes/año (p. ej. desde el widget), se omite
    // y el backend devuelve todos los períodos.
    buildFilterParams() {
      const payload = {}
      if (this.filters.year)      payload.periodYear  = this.filters.year
      if (this.filters.month)     payload.periodMonth = this.filters.month
      if (this.filters.companyId) payload.companyId = this.filters.companyId
      if (this.filters.type)      payload.type      = this.filters.type
      if (this.filters.conceptId) payload.conceptId = this.filters.conceptId
      if (this.filters.status)    payload.status    = this.filters.status
      return payload
    },
    // loadData es el hook que dispara FilterMixin al aplicar/borrar filtros: enruta según la vista.
    async loadData() {
      if (this.showGroups) return this.loadGroups()
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.getByPeriod(this.buildFilterParams())
        this.list = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los movimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadGroups() {
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = {}
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        const resp = await PayrollMovementService.getInstallmentGroups(payload)
        this.groups = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los grupos de cuotas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    onToggleGroups() { this.loadData() },
    async downloadExcel() {
      try {
        this.downloading = true
        const payload = this.buildFilterParams()
        const resp = await PayrollMovementService.downloadListXlsx(payload)
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        const suffix = (payload.periodYear && payload.periodMonth)
          ? `${payload.periodYear}-${String(payload.periodMonth).padStart(2, '0')}`
          : 'todos'
        a.download = `movimientos-${suffix}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el Excel')
      } finally {
        this.downloading = false
      }
    },
    async downloadTemplate() {
      try {
        this.downloadingTemplate = true
        const resp = await PayrollMovementService.downloadBulkTemplate()
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = 'plantilla-movimientos.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar la plantilla')
      } finally {
        this.downloadingTemplate = false
      }
    },
    openUploadModal() {
      this.showUploadModal = true
    },
    getTypeLabel(type) {
      const t = LocalConstants.PayrollConceptType[type]
      return t ? t.label : type
    },
    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },
    getOriginLabel(origin) {
      const o = LocalConstants.PayrollMovementOrigin[origin]
      return o ? o.label : origin
    },
    getGroupStatusLabel(status) {
      const s = LocalConstants.InstallmentGroupStatus[status]
      return s ? s.label : status
    },
    formatAmount(amount, currency) {
      if (amount == null) return '-'
      const formatted = Number(amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${currency || 'S/'} ${formatted}`
    },
    openCancel(item) {
      this.selectedOption = item
      this.openConfirmModal({
        title:       'Cancelar movimiento',
        description: `¿Está seguro que desea cancelar el movimiento de ${item.employeeName} por ${this.formatAmount(item.amount, item.currency)}?`,
        acceptText:  'Cancelar movimiento',
        action:      'cancel',
      })
    },
    movementActions(item) {
      const actions = []
      if (item.status === 'PENDIENTE') {
        actions.push({ key: 'edit',   label: 'Editar' })
        actions.push({ key: 'cancel', label: 'Cancelar', danger: true })
      }
      if (item.groupId) actions.push({ key: 'group',  label: 'Ver grupo de cuotas' })
      if (item.status !== 'PENDIENTE' && !item.groupId) actions.push({ key: 'detail', label: 'Ver detalle' })
      return actions
    },
    handleMovementAction(item, action) {
      if (action.key === 'edit' || action.key === 'detail')
        this.$router.push({ name: 'payroll-movement-edit', params: { id: item.id } })
      if (action.key === 'cancel') this.openCancel(item)
      if (action.key === 'group')
        this.$router.push({ name: 'movement-group-detail', params: { groupId: item.groupId } })
    },
    onModalConfirm() {
      const action = this.confirmModal.action
      this.closeConfirmModal()
      if (action === 'cancel') this.doCancel()
    },
    async doCancel() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PayrollMovementService.cancel(this.selectedOption.id)
        await this.loadData()
        Alert.success('Movimiento cancelado correctamente')
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al cancelar el movimiento')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    try {
      const [conceptsResp, companiesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        CompanyService.listAll(),
      ])
      // key 2 = Concepto, key 0 = Empresa en filterOptions
      this.filterOptions[2].options = (conceptsResp.payload || []).map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.filterOptions[0].options = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    // Desde el widget del dashboard ("movimientos pendientes"): sin filtro de período
    // + estado PENDIENTE, para que la lista coincida con el conteo (todos los períodos).
    // Si el widget venía filtrado por empresa, propagar ese filtro también.
    if (this.$route.query.fromWidget) {
      this.filters = { status: 'PENDIENTE' }
      if (this.$route.query.companyId) {
        this.filters.companyId = Number(this.$route.query.companyId)
      }
    }
    this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.toolbar-divider
  width 1px
  align-self stretch
  background #dee2e6
  margin 4px 2px
</style>
